import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { dataStore } from '../app.component';
import { lastValueFrom } from 'rxjs';

export interface AddressValidationResult {
  isValid: boolean;
  cleanedAddress: string;
  coordinates?: {
    longitude: number;
    latitude: number;
  };
  street?: string;
  postal?: string;
  housenumber?: string;
  city?: string;
  originalAddress: string;
  error?: string;
  requiresConfirmation?: boolean;
}

export interface BulkValidationResult {
  validAddresses: AddressValidationResult[];
  invalidAddresses: AddressValidationResult[];
  totalProcessed: number;
  validCount: number;
  invalidCount: number;
}

interface GeocodingFeature {
  geometry: {
    coordinates: [number, number]; // [longitude, latitude]
  };
  properties: {
    label: string;
    name: string;
    country: string;
    confidence: number;
    match_type?: string;
    layer?: string;
    postalcode?: string;
    street?: string;
    housenumber?: string;
    locality?: string;
  };
}

interface GeocodingResponse {
  features: GeocodingFeature[];
}

@Injectable({
  providedIn: 'root',
})
export class AddressValidationService {
  private readonly confirmableConfidenceThreshold = 0.6;
  private readonly validConfidenceThreshold = 1;
  private readonly maxRetries = 2;
  private readonly retryableStatusCodes = new Set([408, 425, 429, 500, 502, 503, 504]);
  // Signal to track validation state
  private validationInProgress = signal<boolean>(false);
  private bulkValidationInProgress = signal<boolean>(false);
  private lastValidationResult = signal<AddressValidationResult | null>(null);
  private bulkValidationProgress = signal<number>(0);
  private readonly inFlightValidations = new Map<string, Promise<AddressValidationResult>>();

  // Computed signal for validation status
  isValidating = computed(() => this.validationInProgress());
  isBulkValidating = computed(() => this.bulkValidationInProgress());
  lastResult = computed(() => this.lastValidationResult());
  bulkProgress = computed(() => this.bulkValidationProgress());

  // Dutch postal code regex: 4 digits followed by 2 letters (with or without space)
  private readonly dutchPostalCodeRegex = /\b\d{4}\s?[A-Za-z]{2}\b/;
  private readonly strictDutchPostalCodeRegex = /^[1-9]\d{3}\s?[A-Za-z]{2}$/;
  // Dutch house number addition regex: max 4 alphanumeric characters
  private readonly dutchHouseNumberAdditionRegex = /^[A-Za-z0-9]{1,4}$/;

  constructor(private http: HttpClient) {}

  /**
   * Validates if a Dutch postal code is in the correct format
   * Format: 4 digits (first digit cannot be 0) followed by 2 letters
   * Examples: 1234AB, 1234 AB
   * @param postalCode - The postal code to validate
   * @returns boolean indicating if the postal code is valid
   */
  isValidDutchPostalCode(postalCode: string): boolean {
    if (!postalCode || typeof postalCode !== 'string') {
      return false;
    }

    const trimmed = postalCode.trim();

    // Check if it matches the Dutch postal code format
    if (!this.strictDutchPostalCodeRegex.test(trimmed)) {
      return false;
    }

    // Additional validation: first digit should not be 0
    const firstDigit = parseInt(trimmed.charAt(0), 10);
    if (firstDigit === 0) {
      return false;
    }

    return true;
  }

  /**
   * Validates if a Dutch house number addition is in the correct format
   * Format: 1-4 alphanumeric characters (letters and/or numbers)
   * Examples: A, B, 1, 2, bis, rood, zw
   * @param addition - The house number addition to validate
   * @returns boolean indicating if the addition is valid
   */
  isValidDutchHouseNumberAddition(addition: string): boolean {
    if (!addition || typeof addition !== 'string') {
      return false;
    }

    const trimmed = addition.trim();

    // Check if it matches the Dutch house number addition format
    // Max 4 characters, only letters and numbers allowed
    return this.dutchHouseNumberAdditionRegex.test(trimmed);
  }

  /**
   * Extracts postal code from an address string
   * @param address - The address string
   * @returns The postal code if found, null otherwise
   */
  extractPostalCode(address: string): string | null {
    if (!address || typeof address !== 'string') {
      return null;
    }

    const match = address.match(this.dutchPostalCodeRegex);
    return match ? match[0].trim() : null;
  }

  /**
   * Extracts house number addition from an address string
   * Handles formats like: "Street 12A", "Street 12-1", "Street 12 bis", etc.
   * @param address - The address string
   * @returns The house number addition if found, null otherwise
   */
  extractHouseNumberAddition(address: string): string | null {
    if (!address || typeof address !== 'string') {
      return null;
    }

    // Temporarily remove postal code to avoid false positives
    // e.g. "Abc 12, 1234 AB" -> 1234 AB could be seen as house # + addition
    let searchString = address;
    const postalCode = this.extractPostalCode(address);
    if (postalCode) {
      searchString = address.replace(postalCode, '');
    }

    // Pattern to match house number followed by addition
    // We capture broadly (until space/comma) to catch invalid formats too
    // so they can be validated (and rejected) later
    const patterns = [
      /\b\d+\s*-\s*([^\s,]+)/, // "12-1", "12-invalid"
      /\b\d+\s+([^\s,]+)/, // "12 bis", "12 A", "38 kr.10"
      /\b\d+([^\s,]+)/, // "12A", "12bis", "12invalid"
    ];

    for (const pattern of patterns) {
      const match = searchString.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    return null;
  }

  /**
   * Normalizes a postal code by removing spaces and converting to uppercase
   * @param postalCode - The postal code to normalize
   * @returns Normalized postal code
   */
  private normalizePostalCode(postalCode: string): string {
    if (!postalCode || typeof postalCode !== 'string') {
      return '';
    }
    return postalCode.replace(/\s+/g, '').toUpperCase();
  }

  /**
   * Cleans an address string for API consumption
   * Removes special characters and formats Dutch postal codes
   * @param address - The address string to clean
   * @returns Cleaned address string
   */
  cleanAddressForApi(address: string): string {
    if (!address || typeof address !== 'string') {
      return '';
    }

    let cleanedAddress = address.trim();

    // Remove special characters like ™, ®, ©
    cleanedAddress = cleanedAddress.replace(/[™®©]/g, '');

    // Handle Dutch postal code format: remove space between digits and letters
    // Example: "6641 BK" -> "6641BK"
    cleanedAddress = cleanedAddress.replace(/(\d{4})\s+([A-Za-z]{2})/g, '$1$2');

    // Clean up extra spaces and commas
    cleanedAddress = cleanedAddress
      .replace(/\s+/g, ' ') // Multiple spaces -> single space
      .replace(/,\s*,/g, ',') // Multiple commas -> single comma
      .replace(/,\s*$/, '') // Remove trailing comma
      .trim();

    return cleanedAddress;
  }

  normalizeAddressForComparison(address: string): string {
    if (!address || typeof address !== 'string') {
      return '';
    }

    return this.cleanAddressForApi(address)
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private extractHouseNumber(address: string): number | null {
    const postalCode = this.extractPostalCode(address);
    const addressWithoutPostalCode = postalCode ? address.replace(postalCode, '') : address;
    const match = addressWithoutPostalCode.match(/\b(\d+)\b/);

    return match ? Number.parseInt(match[1], 10) : null;
  }

  private shouldRetryWithoutCity(response: GeocodingResponse | null | undefined): boolean {
    const firstFeature = response?.features?.[0];
    const hasStreetOrAddressResult =
      response?.features?.some(
        (feature) =>
          feature.properties.layer === 'street' || feature.properties.layer === 'address',
      ) ?? false;

    return (
      firstFeature?.properties.match_type === 'fallback' &&
      firstFeature.properties.layer === 'locality' &&
      !hasStreetOrAddressResult
    );
  }

  private removeCityFromAddress(address: string, city: string | undefined): string {
    if (!city) {
      return address;
    }

    return address
      .replace(new RegExp(`\\b${this.escapeRegExp(city)}\\b`, 'i'), '')
      .replace(/\s+,/g, ',')
      .replace(/,\s*$/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private preferStreetOrAddressFeature(features: GeocodingFeature[]): GeocodingFeature | null {
    return (
      features.find((feature) => feature.properties.layer === 'address') ??
      features.find((feature) => feature.properties.layer === 'street') ??
      features[0] ??
      null
    );
  }

  private selectBestRetryFeature(
    features: GeocodingFeature[],
    requestedAddress: string,
  ): GeocodingFeature {
    const requestedCity = this.extractCity(requestedAddress);
    const cityMatches = requestedCity
      ? features.filter(
          (feature) =>
            this.normalizeStreetName(feature.properties.locality ?? '') === requestedCity,
        )
      : features;

    if (requestedCity && cityMatches.length === 0) {
      return features[0];
    }

    const requestedStreet = this.extractStreetName(requestedAddress);
    const streetOrAddressFeatures = cityMatches.filter(
      (feature) => feature.properties.layer === 'address' || feature.properties.layer === 'street',
    );
    const streetMatches = requestedStreet
      ? streetOrAddressFeatures.filter((feature) =>
          this.normalizeStreetName(feature.properties.street || feature.properties.name).includes(
            requestedStreet,
          ),
        )
      : [];
    const candidates = streetMatches.length > 0 ? streetMatches : streetOrAddressFeatures;
    const houseCandidates = candidates.filter((feature) => feature.properties.housenumber);

    return (
      this.sortByClosestHouseNumber(
        houseCandidates.length > 0 ? houseCandidates : candidates,
        this.extractHouseNumber(requestedAddress),
      )[0] ?? features[0]
    );
  }

  private extractCity(address: string): string | null {
    const postalCodeMatch = address.match(this.dutchPostalCodeRegex);
    if (!postalCodeMatch || postalCodeMatch.index === undefined) {
      return null;
    }

    const city = address
      .slice(postalCodeMatch.index + postalCodeMatch[0].length)
      .replace(/^[\s,]+/, '')
      .trim();

    return city ? this.normalizeStreetName(city) : null;
  }

  private sortByClosestHouseNumber(
    features: GeocodingFeature[],
    requestedHouseNumber: number | null,
  ): GeocodingFeature[] {
    if (requestedHouseNumber === null || features.length < 2) {
      return features;
    }

    return [...features].sort((left, right) => {
      const leftDistance = this.getHouseNumberDistance(left, requestedHouseNumber);
      const rightDistance = this.getHouseNumberDistance(right, requestedHouseNumber);

      return leftDistance - rightDistance;
    });
  }

  private getHouseNumberDistance(feature: GeocodingFeature, requestedHouseNumber: number): number {
    const featureHouseNumber = feature.properties.housenumber?.match(/\d+/)?.[0];

    if (!featureHouseNumber) {
      return Number.POSITIVE_INFINITY;
    }

    return Math.abs(Number.parseInt(featureHouseNumber, 10) - requestedHouseNumber);
  }

  private formatStreetCityAddress(feature: GeocodingFeature): string {
    return [feature.properties.street || feature.properties.name, feature.properties.locality]
      .filter(Boolean)
      .join(', ');
  }

  private extractStreetName(address: string): string {
    const withoutPostalCode = this.extractPostalCode(address)
      ? address.replace(this.extractPostalCode(address)!, '')
      : address;
    const streetName = withoutPostalCode.split(/\d/)[0] ?? '';

    return this.normalizeStreetName(streetName);
  }

  private normalizeStreetName(streetName: string): string {
    return streetName
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private isValidCoordinates(coordinates: { longitude: number; latitude: number }): boolean {
    return (
      Number.isFinite(coordinates.longitude) &&
      Number.isFinite(coordinates.latitude) &&
      coordinates.latitude !== 0 &&
      coordinates.longitude !== 0 &&
      coordinates.latitude >= -90 &&
      coordinates.latitude <= 90 &&
      coordinates.longitude >= -180 &&
      coordinates.longitude <= 180
    );
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Validates an address using OpenRouteService geocoding API
   * Returns a Promise with the validation result
   * @param address - The address to validate (can be string or address object)
   * @param country - The country code to restrict search (default: 'NL')
   * @returns Promise with validation result
   */
  async validateAddress(
    address: string | { address: string },
    country: string = 'NL',
    skipStateManagement: boolean = false,
  ): Promise<AddressValidationResult> {
    const addressString = typeof address === 'string' ? address : address?.address;
    const key = `${country}:${this.normalizeAddressForComparison(addressString)}`;
    const existingRequest = this.inFlightValidations.get(key);
    if (existingRequest) {
      console.info('[AddressValidation] Reusing in-flight ORS validation request', {
        country,
        key,
        address: addressString,
      });
      return existingRequest;
    }

    const request = this.validateAddressInternal(address, country, skipStateManagement);
    this.inFlightValidations.set(key, request);
    try {
      return await request;
    } finally {
      if (this.inFlightValidations.get(key) === request) {
        this.inFlightValidations.delete(key);
      }
    }
  }

  private async validateAddressInternal(
    address: string | { address: string },
    country: string = 'NL',
    skipStateManagement: boolean = false,
  ): Promise<AddressValidationResult> {
    if (!skipStateManagement) {
      this.validationInProgress.set(true);
    }

    try {
      const addressString = typeof address === 'string' ? address : address?.address;
      const cleanedAddress = this.cleanAddressForApi(addressString);

      if (!cleanedAddress) {
        const result: AddressValidationResult = {
          isValid: false,
          cleanedAddress: '',
          originalAddress: addressString,
          error: 'Address is empty or invalid',
        };
        this.lastValidationResult.set(result);
        return result;
      }

      // Check if address contains a Dutch postal code and validate it
      let requestedPostalCode: string | null = null;
      if (country === 'NL') {
        requestedPostalCode = this.extractPostalCode(addressString);
        if (
          (requestedPostalCode && !this.isValidDutchPostalCode(requestedPostalCode)) ||
          !requestedPostalCode
        ) {
          const result: AddressValidationResult = {
            isValid: false,
            cleanedAddress,
            originalAddress: addressString,
            error: requestedPostalCode
              ? `Invalid postal code: ${requestedPostalCode}.`
              : 'No postal code found in address',
          };
          this.lastValidationResult.set(result);
          return result;
        }

        // Check if address contains a house number addition and validate it
        const houseNumberAddition = this.extractHouseNumberAddition(addressString);
        if (houseNumberAddition && !this.isValidDutchHouseNumberAddition(houseNumberAddition)) {
          const result: AddressValidationResult = {
            isValid: false,
            cleanedAddress,
            originalAddress: addressString,
            error: 'Invalid housenumberAddition',
          };
          this.lastValidationResult.set(result);
          return result;
        }
      }

      const apiKey = dataStore.generalSettings?.orsApiKey;
      if (!apiKey) {
        throw new Error('OpenRouteService API key not configured');
      }

      const url = `https://api.openrouteservice.org/geocode/search?api_key=${apiKey}&text=${encodeURIComponent(cleanedAddress)}&boundary.country=${encodeURIComponent(country)}`;

      console.info('[AddressValidation] Requesting ORS geocoding', {
        country,
        cleanedAddress,
      });
      const response = await this.performGeocodingRequestWithRetry(url);
      console.log('addressValidationResponse:', response);

      if (this.shouldRetryWithoutCity(response)) {
        const firstFeature = response.features[0];
        const requestedCity = this.extractCity(addressString);
        const retryAddress = this.removeCityFromAddress(
          cleanedAddress,
          requestedCity || firstFeature.properties.locality || firstFeature.properties.name,
        );

        if (retryAddress && retryAddress !== cleanedAddress) {
          const retryUrl = `https://api.openrouteservice.org/geocode/search?api_key=${apiKey}&text=${encodeURIComponent(retryAddress)}&boundary.country=${encodeURIComponent(country)}`;
          console.info('[AddressValidation] Retrying ORS geocoding without city', {
            country,
            cleanedAddress: retryAddress,
          });
          const retryResponse = await this.performGeocodingRequestWithRetry(retryUrl);
          console.log('addressValidationRetryResponse:', retryResponse);

          if (retryResponse?.features && retryResponse.features.length > 0) {
            const matchingFeature = this.selectBestRetryFeature(
              retryResponse.features,
              addressString,
            );
            const coordinates = {
              longitude: matchingFeature.geometry.coordinates[0],
              latitude: matchingFeature.geometry.coordinates[1],
            };

            if (!this.isValidCoordinates(coordinates)) {
              const result: AddressValidationResult = {
                isValid: false,
                cleanedAddress,
                originalAddress: addressString,
                error: 'No results found for this address (low confidence)',
              };
              this.lastValidationResult.set(result);
              return result;
            }

            const result: AddressValidationResult = {
              isValid: false,
              cleanedAddress: this.formatStreetCityAddress(matchingFeature),
              coordinates,
              street: matchingFeature.properties.street || matchingFeature.properties.name,
              city: matchingFeature.properties.locality,
              originalAddress: addressString,
              requiresConfirmation: true,
            };
            this.lastValidationResult.set(result);
            return result;
          }
        }
      }

      if (response?.features && response.features.length > 0) {
        // Normalize the requested postal code for comparison
        const normalizedRequestedPostal = requestedPostalCode
          ? this.normalizePostalCode(requestedPostalCode)
          : null;

        // Find a feature that matches the requested postal code
        let matchingFeature = null;
        if (normalizedRequestedPostal) {
          const postalMatches = response.features.filter((feature) => {
            const featurePostalCode = feature.properties.postalcode;
            if (!featurePostalCode) {
              return false;
            }
            const normalizedFeaturePostal = this.normalizePostalCode(featurePostalCode);
            return normalizedFeaturePostal === normalizedRequestedPostal;
          });
          matchingFeature = this.preferStreetOrAddressFeature(postalMatches);

          // If no matching postal code found in any feature, validation fails
          if (!matchingFeature) {
            // Some OSM-sourced results don't carry postal code data at all, so fall back to a
            // feature without one instead of rejecting an otherwise correct address.
            matchingFeature = this.preferStreetOrAddressFeature(
              response.features.filter((feature) => !feature.properties.postalcode),
            );
          }

          if (!matchingFeature) {
            console.log(
              'no matching postal code, requested:',
              normalizedRequestedPostal,
              'received:',
              response.features.map((feature) => feature.properties.postalcode),
            );
            const result: AddressValidationResult = {
              isValid: false,
              cleanedAddress,
              originalAddress: addressString,
              error: `No matching postal code found in geocoding results.`,
            };
            this.lastValidationResult.set(result);
            return result;
          }
        } else {
          // If no postal code was extracted, use the first feature (fallback behavior)
          matchingFeature = this.preferStreetOrAddressFeature(response.features);
        }

        // Pelias/ORS rarely returns confidence 1 for interpolated house numbers, so use a lenient
        // threshold. match_type is ignored here: Pelias already deflates confidence for fallback
        // results, and a strict postal code match above already rules out unrelated results.
        console.log(
          'address confidence:',
          matchingFeature.properties.confidence,
          'match_type:',
          matchingFeature.properties.match_type,
        );

        const confidence = matchingFeature.properties.confidence;
        const coordinates = {
          longitude: matchingFeature.geometry.coordinates[0],
          latitude: matchingFeature.geometry.coordinates[1],
        };

        if (!this.isValidCoordinates(coordinates)) {
          const result: AddressValidationResult = {
            isValid: false,
            cleanedAddress,
            originalAddress: addressString,
            error: 'No results found for this address (low confidence)',
          };
          this.lastValidationResult.set(result);
          return result;
        }

        if (
          confidence >= this.confirmableConfidenceThreshold &&
          confidence < this.validConfidenceThreshold
        ) {
          const result: AddressValidationResult = {
            isValid: false,
            cleanedAddress,
            coordinates,
            street: matchingFeature.properties.street || matchingFeature.properties.name,
            postal: matchingFeature.properties.postalcode,
            housenumber: matchingFeature.properties.housenumber,
            city: matchingFeature.properties.locality,
            originalAddress: addressString,
            requiresConfirmation: true,
          };
          this.lastValidationResult.set(result);
          return result;
        }

        if (confidence < this.confirmableConfidenceThreshold) {
          const result: AddressValidationResult = {
            isValid: false,
            cleanedAddress,
            originalAddress: addressString,
            error: 'No results found for this address (low confidence)',
          };
          this.lastValidationResult.set(result);
          return result;
        }

        const result: AddressValidationResult = {
          isValid: true,
          cleanedAddress,
          coordinates,
          street: matchingFeature.properties.street || matchingFeature.properties.name,
          postal: matchingFeature.properties.postalcode,
          housenumber: matchingFeature.properties.housenumber,
          city: matchingFeature.properties.locality,
          originalAddress: addressString,
        };
        this.lastValidationResult.set(result);
        return result;
      } else {
        const result: AddressValidationResult = {
          isValid: false,
          cleanedAddress,
          originalAddress: addressString,
          error: 'No results found for this address',
        };
        this.lastValidationResult.set(result);
        return result;
      }
    } catch (error: any) {
      const addressString = typeof address === 'string' ? address : address.address;
      const cleanedAddress = this.cleanAddressForApi(addressString);
      const result: AddressValidationResult = {
        isValid: false,
        cleanedAddress,
        originalAddress: addressString,
        error: `API Error: ${error.message || 'Unknown error'}`,
      };
      this.lastValidationResult.set(result);
      return result;
    } finally {
      if (!skipStateManagement) {
        this.validationInProgress.set(false);
      }
    }
  }

  /**
   * Validates multiple addresses in bulk
   * @param addresses - Array of addresses to validate
   * @param country - The country code to restrict search (default: 'NL')
   * @param delayBetweenRequests - Delay in milliseconds between API requests to avoid rate limiting (default: 650ms)
   * @returns Promise with bulk validation results
   */
  async validateAddressesBulk(
    addresses: (string | { address: string })[],
    country: string = 'NL',
    delayBetweenRequests: number = 650,
  ): Promise<BulkValidationResult> {
    this.bulkValidationInProgress.set(true);
    this.bulkValidationProgress.set(0);

    const validAddresses: AddressValidationResult[] = [];
    const invalidAddresses: AddressValidationResult[] = [];
    const bulkValidationCache = new Map<string, AddressValidationResult>();

    try {
      for (let i = 0; i < addresses.length; i++) {
        const address = addresses[i];
        let usedCachedResult = false;

        // Update progress
        this.bulkValidationProgress.set(Math.round(((i + 1) / addresses.length) * 100));

        try {
          const addressString = typeof address === 'string' ? address : address?.address;
          const cacheKey = `${country}:${this.normalizeAddressForComparison(addressString)}`;
          const cachedResult = bulkValidationCache.get(cacheKey);
          const result = cachedResult
            ? { ...cachedResult, originalAddress: addressString }
            : await this.validateAddress(address, country, true);
          usedCachedResult = !!cachedResult;

          if (!cachedResult) {
            bulkValidationCache.set(cacheKey, result);
          }

          if (result.isValid) {
            validAddresses.push(result);
          } else {
            invalidAddresses.push(result);
          }
        } catch (error: any) {
          // If individual validation fails, add to invalid list
          const addressString = typeof address === 'string' ? address : address.address;
          invalidAddresses.push({
            isValid: false,
            cleanedAddress: this.cleanAddressForApi(addressString),
            originalAddress: addressString,
            error: `Validation failed: ${error.message || 'Unknown error'}`,
          });
        }

        // Add delay between requests to avoid rate limiting (except for last item)
        if (i < addresses.length - 1 && !usedCachedResult) {
          await this.delay(delayBetweenRequests);
        }
      }

      return {
        validAddresses,
        invalidAddresses,
        totalProcessed: addresses.length,
        validCount: validAddresses.length,
        invalidCount: invalidAddresses.length,
      };
    } finally {
      this.bulkValidationInProgress.set(false);
      this.bulkValidationProgress.set(0);
    }
  }

  /**
   * Helper method to add delay between API requests
   * @param ms - Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async performGeocodingRequestWithRetry(url: string): Promise<GeocodingResponse> {
    let attempt = 0;

    while (true) {
      try {
        return await lastValueFrom(this.http.get<GeocodingResponse>(url));
      } catch (error) {
        if (!this.shouldRetryRequest(error, attempt)) {
          console.warn('[AddressValidation] ORS request failed without retry', {
            attempt,
            message: error instanceof Error ? error.message : String(error),
          });
          throw error;
        }

        const delayMs = this.resolveRetryDelayMs(error, attempt);
        console.warn('[AddressValidation] Retrying ORS request', {
          attempt: attempt + 1,
          delayMs,
          message: error instanceof Error ? error.message : String(error),
        });
        await this.delay(delayMs);
        attempt += 1;
      }
    }
  }

  private shouldRetryRequest(error: unknown, attempt: number): boolean {
    if (attempt >= this.maxRetries) {
      return false;
    }

    if (!(error instanceof HttpErrorResponse)) {
      return true;
    }

    if (error.status === 0) {
      return true;
    }

    return this.retryableStatusCodes.has(error.status);
  }

  private resolveRetryDelayMs(error: unknown, attempt: number): number {
    if (error instanceof HttpErrorResponse) {
      const retryAfterHeader = error.headers?.get('Retry-After');
      const retryAfterDelay = this.parseRetryAfterHeader(retryAfterHeader);
      if (retryAfterDelay !== null) {
        return retryAfterDelay;
      }
    }

    const baseDelayMs = 1000 * 2 ** attempt;
    const jitterMs = Math.floor(Math.random() * 250);
    return Math.min(baseDelayMs + jitterMs, 10000);
  }

  private parseRetryAfterHeader(retryAfter: string | null): number | null {
    if (!retryAfter) {
      return null;
    }

    const seconds = Number.parseInt(retryAfter, 10);
    if (Number.isFinite(seconds)) {
      return Math.max(seconds * 1000, 0);
    }

    const retryDate = Date.parse(retryAfter);
    if (Number.isNaN(retryDate)) {
      return null;
    }

    return Math.max(retryDate - Date.now(), 0);
  }

  /**
   * Extracts coordinates from an address
   * @param address - The address to geocode
   * @param country - The country code to restrict search
   * @returns Promise with coordinates or null
   */
  async getCoordinates(
    address: string | { address: string },
    country: string = 'NL',
  ): Promise<{ longitude: number; latitude: number } | null> {
    const result = await this.validateAddress(address, country);
    return result.coordinates || null;
  }

  /**
   * Simplifies address to street + city only (removes postal code)
   * @param address - The full address string
   * @returns Simplified address
   */
  simplifyAddress(address: string): string {
    if (!address || typeof address !== 'string') {
      return '';
    }

    const parts = address.split(',');
    if (parts.length >= 2) {
      const streetPart = parts[0].trim();
      const cityPart = parts[parts.length - 1].trim();
      // Remove postal code from city part
      const cityWithoutPostal = cityPart.replace(/\d{4}[A-Za-z]{2}\s*/, '').trim();
      return `${streetPart}, ${cityWithoutPostal}`;
    }

    return address.trim();
  }

  /**
   * Checks if an address string appears to be valid (basic format validation)
   * Does not make API calls
   * @param address - The address to check
   * @returns boolean indicating if format appears valid
   */
  hasValidFormat(address: string): boolean {
    if (!address || typeof address !== 'string') {
      return false;
    }

    const trimmed = address.trim();

    // Check for minimum length
    if (trimmed.length < 5) {
      return false;
    }

    // Check for at least one number (street number)
    const hasNumber = /\d/.test(trimmed);

    // Check for at least one letter
    const hasLetter = /[a-zA-Z]/.test(trimmed);

    return hasNumber && hasLetter;
  }

  /**
   * Resets the validation state
   */
  resetValidation(): void {
    this.validationInProgress.set(false);
    this.bulkValidationInProgress.set(false);
    this.lastValidationResult.set(null);
    this.bulkValidationProgress.set(0);
  }
}
